import React, { useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "../config/api";

export default function StaffWorkingDays() {
  const [staff, setStaff] = useState([]);
  const [days, setDays] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  async function load() {
    setLoading(true);
    setMessage("");
    try {
      const res = await fetchWithAuth("/api/staff/working-days/");
      const json = await res.json().catch(() => ({ staff: [], days: [] }));
      setStaff(json.staff || []);
      setDays(json.days || []);
      if (!selectedStaffId && (json.staff || []).length) {
        setSelectedStaffId(String(json.staff[0].id));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedDays = useMemo(
    () => days.filter((day) => String(day.staff_id) === String(selectedStaffId)),
    [days, selectedStaffId]
  );

  function updateDay(dayOfWeek, patch) {
    setDays((prev) =>
      prev.map((day) =>
        String(day.staff_id) === String(selectedStaffId) && Number(day.day_of_week) === Number(dayOfWeek)
          ? { ...day, ...patch }
          : day
      )
    );
  }

  async function save() {
    const payload = {
      days: selectedDays.map((day) => ({
        staff_id: day.staff_id,
        day_of_week: day.day_of_week,
        is_working: Boolean(day.is_working),
        start_time: day.start_time || "11:00",
        end_time: day.end_time || "22:00",
        notes: day.notes || "",
      })),
    };
    const res = await fetchWithAuth("/api/staff/working-days/", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      setMessage(json.error || "Failed to save working days.");
      return;
    }
    setMessage("Working days saved.");
    await load();
  }

  if (loading) return <div>Loading working days...</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Staff Working Days</h3>
        <button className="btn" type="button" onClick={load}>
          Refresh
        </button>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label className="label">Staff</label>
        <select
          className="input"
          value={selectedStaffId}
          onChange={(event) => setSelectedStaffId(event.target.value)}
        >
          {staff.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </div>

      {message && <div style={{ marginBottom: 12 }}>{message}</div>}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: 8 }}>Day</th>
              <th style={{ textAlign: "left", padding: 8 }}>Working</th>
              <th style={{ textAlign: "left", padding: 8 }}>Start</th>
              <th style={{ textAlign: "left", padding: 8 }}>End</th>
              <th style={{ textAlign: "left", padding: 8 }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {selectedDays.map((day) => (
              <tr key={day.day_of_week}>
                <td style={{ padding: 8 }}>{day.day_name}</td>
                <td style={{ padding: 8 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(day.is_working)}
                    onChange={(event) => updateDay(day.day_of_week, { is_working: event.target.checked })}
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <input
                    type="time"
                    value={day.start_time || "11:00"}
                    disabled={!day.is_working}
                    onChange={(event) => updateDay(day.day_of_week, { start_time: event.target.value })}
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <input
                    type="time"
                    value={day.end_time || "22:00"}
                    disabled={!day.is_working}
                    onChange={(event) => updateDay(day.day_of_week, { end_time: event.target.value })}
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <input
                    value={day.notes || ""}
                    onChange={(event) => updateDay(day.day_of_week, { notes: event.target.value })}
                    placeholder="Optional"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 16 }}>
        <button className="btn" type="button" onClick={save}>
          Save Working Days
        </button>
      </div>
    </div>
  );
}
